import type { PageModule } from "./page-module"

/**
 * メンバーインタビューのモーダル制御クラス
 * - カード（[data-interview-open]）クリックで該当スライドを開く
 * - PREV / NEXT（[data-interview-prev] / [data-interview-next]）で巡回（端でループ）
 * - オーバーレイ／閉じるボタン（[data-interview-close]）／Escapeで閉じる
 * bodyスクロールロック・ARIA・inert を管理。jQuery不使用。
 * @example
 * new InterviewModal(scope);
 */
export class InterviewModal {
  modal: HTMLElement | null
  slides: HTMLElement[]
  openers: HTMLElement[]
  current: number
  ac: AbortController

  /**
   * @param {ParentNode} scope - 要素探索の起点（swup対応で現在ページの<main>を渡す。後方互換でdocument）
   */
  constructor(scope: ParentNode = document) {
    this.modal = scope.querySelector("#js-interview-modal")
    this.slides = Array.from(scope.querySelectorAll<HTMLElement>("[data-interview-slide]"))
    this.openers = Array.from(scope.querySelectorAll<HTMLElement>("[data-interview-open]"))
    this.current = 0
    this.ac = new AbortController()

    // モーダルが無いページ（recruit以外）では初期化しない
    if (!this.modal || !this.slides.length) return

    this.bindEvents()
  }

  /**
   * イベントリスナーを登録
   * @private
   */
  bindEvents = () => {
    this.openers.forEach((el) => {
      el.addEventListener(
        "click",
        (e) => {
          e.preventDefault()
          const i = Number(el.dataset.interviewOpen ?? 0)
          this.open(i)
        },
        { signal: this.ac.signal }
      )
    })

    // 閉じる（オーバーレイ・×ボタン）
    this.modal?.querySelectorAll<HTMLElement>("[data-interview-close]").forEach((el) => {
      el.addEventListener("click", this.close, { signal: this.ac.signal })
    })

    // PREV / NEXT（スライドごとにあるのでイベント委譲）
    this.modal?.addEventListener(
      "click",
      (e) => {
        const target = e.target as HTMLElement
        if (target.closest("[data-interview-prev]")) this.go(-1)
        if (target.closest("[data-interview-next]")) this.go(1)
      },
      { signal: this.ac.signal }
    )

    document.addEventListener("keydown", this.onKeydown, { signal: this.ac.signal })
  }

  /**
   * 指定インデックスのスライドでモーダルを開く
   * @public
   * @param {number} index - 表示するスライド番号
   */
  open = (index: number) => {
    this.show(index)
    this.modal?.classList.add("is-show")
    this.modal?.setAttribute("aria-hidden", "false")
    document.body.style.overflow = "hidden"
  }

  /**
   * モーダルを閉じる
   * @public
   */
  close = () => {
    this.modal?.classList.remove("is-show")
    this.modal?.setAttribute("aria-hidden", "true")
    document.body.style.overflow = ""
  }

  /**
   * 相対移動（端でループ）
   * @private
   * @param {number} dir - -1=前 / 1=次
   */
  go = (dir: number) => {
    const len = this.slides.length
    this.show((this.current + dir + len) % len)
  }

  /**
   * 指定スライドをアクティブに（is-active のトグル＝CSSでクロスフェード）
   * @private
   * @param {number} index - 表示するスライド番号
   */
  show = (index: number) => {
    this.current = index
    this.slides.forEach((slide, i) => {
      slide.classList.toggle("is-active", i === index)
    })
  }

  /**
   * キーボード操作（Escapeで閉じる・矢印で巡回）
   * @private
   * @param {KeyboardEvent} e - キーボードイベント
   */
  onKeydown = (e: KeyboardEvent) => {
    if (!this.modal?.classList.contains("is-show")) return
    if (e.key === "Escape") this.close()
    if (e.key === "ArrowLeft") this.go(-1)
    if (e.key === "ArrowRight") this.go(1)
  }

  /**
   * 後始末：全リスナー解除・bodyスクロールロック解除
   * @public
   */
  unmount = () => {
    this.ac.abort()
    document.body.style.overflow = ""
  }
}

/**
 * InterviewModal を現在ページの scope に mount し、destroy 関数を返す
 * @param {HTMLElement} scope - 現在ページの <main>
 * @returns {() => void} 後始末関数
 */
export const mountInterviewModal: PageModule = (scope) => {
  const inst = new InterviewModal(scope)
  return () => inst.unmount()
}
